<script setup lang="ts">
import {
  ArrowLeftRight as ArrowLeftRightIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Redo as RedoIcon,
  Undo as UndoIcon,
} from '@lucide/vue'
import { computed, ref } from 'vue'

import RoutePointList from '@/components/map/toolbar/RoutePointList.vue'
import BaseToolbox from '@/components/ui/BaseToolbox.vue'
import { ROUTING_PROFILES } from '@/services/routingService'
import { useUiStore } from '@/stores/uiStore'
import type { Segment, Tour } from '@/types/tour'
import type { TrackProfileMetricId } from '@/utils/trackProfileAnalysis'

const props = withDefaults(
  defineProps<{
    canReverse: boolean
    canUndo: boolean
    canRedo: boolean
    canSave: boolean
    hasPoints: boolean
    isCalculatingRoute: boolean
    segments: Segment[]
    profileMetric?: TrackProfileMetricId
    /** Draft tour being drawn — drives the "Your Route" route-point list. */
    tour?: Tour | null
    /** Active track being drawn, to scope the list to it. */
    trackClientId?: string | null
  }>(),
  {
    profileMetric: 'gradient',
    tour: null,
    trackClientId: null,
  },
)

const emit = defineEmits<{
  (e: 'reverse'): void
  (e: 'undo'): void
  (e: 'redo'): void
  (e: 'save'): void
  (e: 'cancel'): void
  (e: 'routing-profile-change', profile: string): void
  (e: 'profile-metric-change', metric: TrackProfileMetricId): void
  (e: 'zoom-route-point', tourId: string, routePointIndex: number): void
  (
    e: 'reorder-route-points',
    tourId: string,
    trackClientId: string | null,
    orderedRoutePointClientIds: string[],
  ): void
}>()

const uiStore = useUiStore()
const isCollapsed = ref(false)

const totalDistanceM = computed(() =>
  props.segments.reduce((sum, s) => sum + (s.trackLength ?? 0), 0),
)
const totalAscent = computed(() =>
  props.segments.reduce((sum, s) => sum + (s.filteredAscend ?? 0), 0),
)
const totalDescent = computed(() =>
  props.segments.reduce((sum, s) => sum + (s.filteredDescend ?? 0), 0),
)

const formatDistance = (m: number) =>
  m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`

const profileMetricOptions: Array<{ id: TrackProfileMetricId; label: string }> = [
  { id: 'gradient', label: 'Gradient' },
  { id: 'surface', label: 'Surface' },
  { id: 'wayType', label: 'Way type' },
]
</script>

<template>
  <BaseToolbox
    :is-open="true"
    title="Track routing"
    :is-collapsed="isCollapsed"
    :collapsible="true"
    :closeable="false"
    elevated
    :initial-offset-y="240"
    panel-class="w-[420px] animate-in slide-in-from-bottom-4 duration-200"
    body-class="space-y-3"
    data-testid="track-routing-toolbox"
    @update:is-collapsed="isCollapsed = $event"
  >
    <div class="text-sm font-medium text-gray-700 text-center">
      {{ hasPoints ? 'Click map to extend track' : 'Click map to start track' }}
    </div>

    <div
      v-if="hasPoints"
      class="flex items-center justify-center gap-3 text-xs text-gray-700 font-medium"
    >
      <span>{{ formatDistance(totalDistanceM) }}</span>
      <span v-if="totalAscent > 0" class="text-green-700">↑ {{ Math.round(totalAscent) }}m</span>
      <span v-if="totalDescent > 0" class="text-orange-700">↓ {{ Math.round(totalDescent) }}m</span>
    </div>

    <!-- "Your Route" — the route-point list, so anchors can be reviewed / deleted from a list
         (essential on mobile where points can sit behind the panel). -->
    <div
      v-if="hasPoints && tour"
      class="max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50/60"
    >
      <RoutePointList
        :tour="tour"
        :track-client-id="trackClientId"
        :expanded="true"
        :can-delete-route-points="true"
        :reorderable="true"
        :hide-track-headers="true"
        size="comfortable"
        @zoom-route-point="(tourId, index) => emit('zoom-route-point', tourId, index)"
        @reorder-route-points="
          (tourId, trackClientId, ids) => emit('reorder-route-points', tourId, trackClientId, ids)
        "
      />
    </div>

    <div class="flex w-full flex-wrap items-center justify-center gap-2">
      <select
        :value="uiStore.routingProfile"
        @change="emit('routing-profile-change', ($event.target as HTMLSelectElement).value)"
        class="min-w-0 flex-1 text-xs px-2 py-1 rounded-md border border-gray-300 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary-500"
        title="Routing profile"
      >
        <option v-for="p in ROUTING_PROFILES" :key="p.id" :value="p.id">{{ p.label }}</option>
      </select>

      <div class="flex w-full items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1">
        <span class="px-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
          Color by
        </span>
        <button
          v-for="option in profileMetricOptions"
          :key="option.id"
          type="button"
          :disabled="isCalculatingRoute"
          :class="[
            'min-w-0 flex-1 rounded-md px-2 py-1 text-xs font-medium transition-colors hover:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
            props.profileMetric === option.id
              ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
              : 'text-gray-600 hover:bg-white/80 hover:text-gray-900',
          ]"
          :aria-pressed="props.profileMetric === option.id"
          @click="emit('profile-metric-change', option.id)"
        >
          {{ option.label }}
        </button>
      </div>

      <button
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canReverse || isCalculatingRoute"
        title="Reverse route"
        @click="emit('reverse')"
      >
        <ArrowLeftRightIcon class="w-4 h-4" />
        <span>Reverse route</span>
      </button>

      <button
        class="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canUndo || isCalculatingRoute"
        title="Undo last segment"
        @click="emit('undo')"
      >
        <UndoIcon class="w-4 h-4" />
      </button>

      <button
        class="p-2 rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canRedo || isCalculatingRoute"
        title="Redo last segment"
        @click="emit('redo')"
      >
        <RedoIcon class="w-4 h-4" />
      </button>

      <button
        :class="[
          'p-2 rounded-md transition-colors hover:cursor-pointer',
          uiStore.isMapElementsHiddenByButton
            ? 'bg-gray-800 text-white hover:bg-gray-700'
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
        ]"
        :title="
          uiStore.isMapElementsHiddenByButton ? 'Show map elements (M)' : 'Hide map elements (M)'
        "
        @click="uiStore.toggleMapElementsHiddenByButton()"
      >
        <EyeOffIcon v-if="uiStore.isMapElementsHiddenByButton" class="w-4 h-4" />
        <EyeIcon v-else class="w-4 h-4" />
      </button>
    </div>

    <div class="flex w-full items-center justify-center gap-3 pt-1">
      <button
        class="min-w-[120px] px-4 py-1.5 rounded-md bg-white border border-gray-300 text-gray-700 text-sm font-medium shadow-sm hover:bg-gray-50 transition-colors hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="isCalculatingRoute"
        @click="emit('cancel')"
      >
        Cancel
      </button>

      <button
        class="min-w-[120px] px-4 py-1.5 rounded-md bg-primary-600 text-white text-sm font-medium shadow-sm hover:bg-primary-700 transition-colors hover:cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
        :disabled="!canSave || isCalculatingRoute"
        @click="emit('save')"
      >
        Save
      </button>
    </div>
  </BaseToolbox>
</template>
